import { useState } from 'react';
import { withBase } from '../../../lib/withBase';

export type PageToggleTarget = 'about' | 'playground';

export function PageToggle({
  active,
  variant = 'light',
  onNavigate,
}: {
  active: PageToggleTarget;
  variant?: 'light' | 'dark';
  /**
   * Optional click handler for the inactive button. When provided, it
   * fully replaces the default `<a href>` navigation (caller is
   * responsible for whatever should happen — e.g. in-page view swap).
   *
   * When NOT provided and the toggle is rendered inside an iframe
   * (window.parent !== window), the click is forwarded to the parent
   * frame as a `mlipview:navigate` postMessage so the embedding page
   * can handle the swap without a hard reload. Top-frame default is
   * normal link navigation.
   */
  onNavigate?: (target: PageToggleTarget) => void;
}) {
  const dark = variant === 'dark';
  const [hovered, setHovered] = useState(false);

  const handleClick = (target: PageToggleTarget) => (e: React.MouseEvent) => {
    if (active === target) {
      e.preventDefault();
      return;
    }
    if (onNavigate) {
      e.preventDefault();
      onNavigate(target);
      return;
    }
    // No explicit handler — if we're inside an iframe, ask parent to
    // swap views in-place. Otherwise fall through to the default <a>
    // navigation.
    if (typeof window !== 'undefined' && window.parent && window.parent !== window) {
      e.preventDefault();
      try {
        // Target our own origin (not '*'): the landing page only ever embeds
        // /playground.html as a same-origin child, so a wildcard target would
        // pointlessly leak the navigate event to any cross-origin parent that
        // happens to frame this view.
        window.parent.postMessage(
          { type: 'mlipview:navigate', target },
          window.location.origin,
        );
      } catch {
        // ignore — fall back to no-op (we already prevented default)
      }
    }
  };

  return (
    <div
      className="transition-opacity duration-200"
      style={{ opacity: dark ? 1 : hovered ? 1 : 0.5 }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div
        className={`flex items-center rounded-full p-1 transition-all duration-150 ${
          dark ? 'bg-gray-900' : 'bg-white/70 backdrop-blur-md'
        }`}
        style={{
          boxShadow: hovered
            ? `0 2px 8px rgba(0,0,0,0.15), inset 0 0 0 2px #1C6DF9`
            : dark
              ? '0 2px 8px rgba(0,0,0,0.3), inset 0 0 0 2px rgba(255,255,255,0.3)'
              : '0 2px 8px rgba(0,0,0,0.1), inset 0 0 0 1px rgb(180,181,184)',
          transition: 'box-shadow 150ms ease',
        }}
      >
        <a
          href={withBase('/')}
          className={`px-5 py-1.5 rounded-full text-[14px] font-medium transition-colors duration-150 ${
            active === 'about'
              ? 'bg-white cursor-default'
              : dark ? 'text-white/70 hover:text-white' : 'hover:text-[#1C6DF9]/80'
          }`}
          style={active === 'about'
            ? { color: '#1C6DF9', boxShadow: dark ? '0 1px 2px rgba(0,0,0,0.1)' : '0 0 0 1px rgba(0,0,0,0.08), 0 0 6px rgba(0,0,0,0.15)' }
            : (dark ? undefined : { color: '#1C6DF9' })}
          onClick={handleClick('about')}
        >
          about
        </a>
        <a
          href={withBase('/playground.html')}
          className={`px-5 py-1.5 rounded-full text-[14px] font-medium transition-colors duration-150 ${
            active === 'playground'
              ? 'bg-white cursor-default'
              : dark ? 'text-white/70 hover:text-white' : 'hover:text-[#1C6DF9]/80'
          }`}
          style={active === 'playground'
            ? { color: '#1C6DF9', boxShadow: dark ? '0 1px 2px rgba(0,0,0,0.1)' : '0 0 0 1px rgba(0,0,0,0.08), 0 0 6px rgba(0,0,0,0.15)' }
            : (dark ? undefined : { color: '#1C6DF9' })}
          onClick={handleClick('playground')}
        >
          playground
        </a>
      </div>
    </div>
  );
}
